import { memo, useCallback, useRef, forwardRef } from 'react'
import Link from 'next/link'
//import CompareToggle from '../compare-toggle'
import { NotificationType, VehicleStatus } from '../../api-client/enums'
import truncateString from '../../utils/truncate-string'
import useNotifications from '../../hooks/use-notifications'
import FavoriteToggle from '../favorite-toggle'
import Image from '../image'
import { now } from '../../utils/date-service'
import { MAX_AGE_OF_VEHICLE_FOR_LEASE } from '../../constants'

const minSupportedLeaseYear = now().getFullYear() - MAX_AGE_OF_VEHICLE_FOR_LEASE

const paymentsOptions = {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}

const GridItem = forwardRef(
  (
    {
      id,
      size = 'md',
      imageFileKey,
      name,
      trim,
      mileage,
      price,
      leaseAmount,
      financeAmount,
      // compareIndex,
      isFavorite,
      onFavorite,
      isFavoriteProcessing,
      href,
      status,
      ribbon,
      year,
    },
    ref,
  ) => {
    const linkRef = useRef()
    const { markNotificationAsRead } = useNotifications(NotificationType.Favorite)

    const handleFavoriteClick = useCallback(
      (e) => {
        onFavorite(id, isFavorite)
        markNotificationAsRead(id)
        e.stopPropagation()
      },
      [id, isFavorite, onFavorite, markNotificationAsRead],
    )

    const handleVehicleImageClick = () => linkRef.current.click()

    return (
      <div className='cardGridItem' ref={ref} onClick={handleVehicleImageClick}>
        {ribbon && <div className='ribbon flex alignCenter'>{ribbon}</div>}
        <FavoriteToggle
          size={size}
          onClick={handleFavoriteClick}
          isFavorite={isFavorite}
          isFavoriteProcessing={isFavoriteProcessing}
        />

        <div className='image'>
          <Image src={imageFileKey} alt={name} width='800' height='500' />

          {/* TODO: connect to the flow once the compare module is ready */}
          {/* {<div className='compareToggleContainer'>
            <CompareToggle
              size={size}
              index={compareIndex}
              inputProps={{ 'aria-label': 'compare', name: 'test' }}
            />
          </div>} */}
        </div>

        <div className='details flex'>
          <div className='makeContainer flex flexDirectionColumn'>
            <Link legacyBehavior href={href}>
              <a className='name' ref={linkRef}>
                {truncateString(name)}
              </a>
            </Link>
            <span className='trim'>{truncateString(trim)}</span>

            <p className='mileage'>{mileage.toLocaleString()} miles</p>
          </div>

          <div className='priceContainer flex flexDirectionColumn'>
            <p className='price'>${price.toLocaleString()}</p>

            <div className='financials flex alignStart'>
              <div className='labels'>
                <p className='label financeAmount'>Finance</p>
                {year >= minSupportedLeaseYear && <p className='label lease'>Lease</p>}
              </div>

              <div className='values'>
                <p className='value financeAmount'>
                  <b>
                    {financeAmount > 0
                      ? `${financeAmount.toLocaleString('en-US', paymentsOptions)}/mo`
                      : 'N/A'}
                  </b>
                </p>
                {year >= minSupportedLeaseYear && (
                  <p className='value lease'>
                    <b>
                      {leaseAmount > 0
                        ? `${leaseAmount.toLocaleString('en-US', paymentsOptions)}/mo`
                        : 'N/A'}
                    </b>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          .cardGridItem {
            position: relative;
            overflow: visible;
            width: 100%;
            border-radius: 1rem;
            box-shadow: 0 0.3rem 0.6rem rgba(0, 0, 0, 0.16);
            background: #fff;
            z-index: 1;
            cursor: pointer;

            :global(.favoriteToggle) {
              position: absolute;
              z-index: 10;
            }

            .image {
              position: relative;
              border-radius: 1rem 1rem 0 0;
              overflow: hidden;
              cursor: pointer;

              .compareToggleContainer {
                position: absolute;
              }

              :global(img) {
                display: block;
                max-width: 100%;
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
            }

            .makeContainer {
              width: 100%;

              .name {
                font-weight: 700;
                color: var(--secondary-bg-color);
              }

              .mileage {
                margin: auto 0 0;
                color: var(--secondary-text-color);
              }
            }

            .priceContainer {
              align-items: flex-end;

              .price {
                color: var(--primary-color);
              }

              .financials {
                .labels {
                  color: var(--secondary-text-color);
                }

                .values {
                  text-align: right;
                  color: var(--secondary-bg-color);
                }
              }
            }

            .trim {
              color: var(--secondary-text-color);
              font-size: 1.6rem;
              font-weight: 400;
            }
          }

          .ribbon {
            position: absolute;
            top: 1.1rem;
            left: -0.5rem;
            z-index: 1;
            height: 3.8rem;
            padding: 0 2rem 0 1rem;
            border-radius: 1rem 4rem 4rem 1rem;
          }
        `}</style>

        <style jsx>{`
          .cardGridItem {
            max-width: ${size === 'lg' ? 89.3 : 44.4}rem;
            height: ${size === 'lg' ? 76.8 : 38.9}rem;
            font-size: ${size === 'lg' ? 3.4 : 1.8}rem;

            :global(.favoriteToggle) {
              top: ${size === 'lg' ? 2.4 : 1.2}rem;
              right: ${size === 'lg' ? 3.4 : 1.7}rem;
            }

            .image {
              height: ${size === 'lg' ? 50.2 : 25}rem;

              .compareToggleContainer {
                top: ${size === 'lg' ? 47 : 23.4}rem;
                left: ${size === 'lg' ? 3 : 1.5}rem;
              }
            }

            .details {
              padding: ${size === 'lg' ? '4rem 3.6rem 3.1rem 4rem' : '1.9rem 1.3rem 1.5rem 2rem'};
            }

            .makeContainer {
              padding: ${size === 'lg' ? '1.2rem 0 0' : '1rem 0 0'};
            }

            .priceContainer {
              .price {
                margin: ${size === 'lg' ? '0 0 1.2rem' : '0 0 0.5rem'};
                font-size: ${size === 'lg' ? 6.2 : 3.2}rem;
              }

              .financials {
                .lease,
                .financeAmount {
                  margin: ${size === 'lg' ? '1.2rem 0 0' : '0.5rem 0 0'};
                }

                .values {
                  margin: ${size === 'lg' ? '0 0 0 1.7rem' : '0 0 0 0.7rem'};
                }
              }
            }
          }

          .ribbon {
            background: var(--primary-color);
          }

          @media (max-width: 1024px) {
            .cardGridItem {
              height: 31.2rem;

              .image {
                height: 19.8rem;
              }

              .makeContainer {
                padding: 0;

                .name {
                  font-size: 1.4rem;
                }

                .trim {
                  font-size: 1.3rem;
                }

                .mileage {
                  font-size: 1.4rem;
                }
              }

              .priceContainer {
                .price {
                  font-size: 2.4rem;
                }
              }
            }

            .financials {
              .lease,
              .financeAmount {
                font-size: 1.4rem;
              }
            }

            .details {
              padding: 2rem 2rem 1.2rem;
            }

            .ribbon {
              height: 3.4rem;
              font-size: 1.2rem;
            }
          }
        `}</style>
      </div>
    )
  },
)

export default memo(GridItem)
